import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Alert,
  Progress,
  Tabs,
  message,
  Spin,
} from 'antd';
import {
  WalletOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  getWalletInfo,
  getPointDetails,
  getExpiringSoonPoints,
} from '../../services/wallet';
import './WalletPage.css';

/**
 * 用户钱包页面
 * 职责：展示用户钱包信息、金蝉值统计、金蝉值明细、即将过期的金蝉值
 * 数据：从钱包服务获取钱包信息、统计数据、金蝉值明细列表
 * 交互：刷新数据、查看金蝉值明细、查看即将过期金蝉值
 */
const WalletPage = () => {
  const { isMobile } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [pointDetails, setPointDetails] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 会员等级配置
  const memberLevels = [
    { level: 1, name: '入门会员', points: 0, color: '#8c8c8c' },
    { level: 2, name: '标准会员', points: 1000, color: '#1890ff' },
    { level: 3, name: '黄金会员', points: 5000, color: '#faad14' },
    { level: 4, name: '铂金会员', points: 20000, color: '#722ed1' },
    { level: 5, name: '黑金会员', points: 50000, color: '#262626' },
  ];

  // 获取当前会员等级信息
  const getCurrentMemberLevel = (totalPoints) => {
    let currentLevel = memberLevels[0];
    let nextLevel = null;

    for (let i = 0; i < memberLevels.length; i++) {
      if (totalPoints >= memberLevels[i].points) {
        currentLevel = memberLevels[i];
        if (i < memberLevels.length - 1) {
          nextLevel = memberLevels[i + 1];
        }
      }
    }
    return { currentLevel, nextLevel };
  };

  // 获取升级进度百分比
  const getUpgradeProgress = (currentPoints, currentLevel, nextLevel) => {
    if (!nextLevel) return 100;
    const range = nextLevel.points - currentLevel.points;
    const progress = currentPoints - currentLevel.points;
    return Math.min(100, Math.max(0, Math.round((progress / range) * 100)));
  };

  // 加载钱包数据
  const loadWalletData = async () => {
    setLoading(true);
    try {
      const [infoRes, expiringRes] = await Promise.all([
        getWalletInfo(),
        getExpiringSoonPoints(30),
      ]);

      if (infoRes?.data) setWalletInfo(infoRes.data);
      if (expiringRes?.data) setExpiringSoon(expiringRes.data || []);
    } catch (error) {
      message.error(error.message || '加载钱包数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载金蝉值明细
  const loadPointDetails = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await getPointDetails({ page, pageSize });
      if (res?.data) {
        setPointDetails(res.data.records || []);
        setPagination({
          current: page,
          pageSize,
          total: res.data.total || 0,
        });
      }
    } catch (error) {
      message.error(error.message || '加载金蝉值明细失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadWalletData();
    loadPointDetails(1, 10);
  }, []);

  // 处理分页变化
  const handleTableChange = (pagination) => {
    loadPointDetails(pagination.current, pagination.pageSize);
  };

  // 金蝉值状态渲染
  const renderPointStatus = (status) => {
    const statusMap = {
      ACTIVE: { text: '有效', color: 'success' },
      EXPIRED: { text: '已过期', color: 'default' },
      USED_UP: { text: '已用完', color: 'default' },
      CORRECTION: { text: '冲销', color: 'error' },
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 来源类型渲染
  const renderSource = (source) => {
    const sourceMap = {
      RECHARGE: '充值',
      GIFT: '赠送',
      REFUND: '退款返还',
      ADMIN: '管理员调整',
      SYSTEM: '系统调整',
      CORRECTION: '冲销',
    };
    return sourceMap[source] || source;
  };

  // 金蝉值明细表格列
  const detailColumns = [
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: renderSource,
    },
    {
      title: '金蝉值变动',
      dataIndex: 'points',
      key: 'points',
      width: 100,
      render: (points, record) => {
        const isNegative = points < 0;
        const value = Math.abs(points);
        return (
          <span className={isNegative ? 'points-negative' : 'points-positive'}>
            {isNegative ? '-' : '+'}{value}
          </span>
        );
      },
    },
    {
      title: '金蝉值余额',
      dataIndex: 'remainPoints',
      key: 'remainPoints',
      width: 100,
      render: (points) => (
        <span className={points < 100 ? 'points-low' : ''}>{points}</span>
      ),
    },
    {
      title: '有效期至',
      dataIndex: 'expireTime',
      key: 'expireTime',
      width: 120,
      render: (time) => (time ? new Date(time).toLocaleDateString('zh-CN') : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: renderPointStatus,
    },
    {
      title: '获得时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (time) => new Date(time).toLocaleString('zh-CN'),
    },
  ];

  // 即将过期金蝉值表格列
  const expiringColumns = [
    {
      title: '剩余金蝉值',
      dataIndex: 'remainPoints',
      key: 'remainPoints',
      render: (points) => (
        <span className="points-warning">
          <ClockCircleOutlined /> {points}
        </span>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expireTime',
      key: 'expireTime',
      render: (time) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      render: renderSource,
    },
  ];

  // 计算会员等级信息（使用金蝉值余额 balance）
  const balance = walletInfo?.balance || 0;
  const balanceExpire = walletInfo?.balanceExpire || 0;
  const { currentLevel, nextLevel } = getCurrentMemberLevel(balance);
  const upgradeProgress = getUpgradeProgress(balance, currentLevel, nextLevel);
  const pointsToNextLevel = nextLevel ? nextLevel.points - balance : 0;

  const tabItems = [
    {
      key: 'overview',
      label: '钱包概览',
      children: (
        <div className="wallet-overview">
          {/* 会员等级卡片 */}
          <Card className="member-level-card" bordered={false}>
            <div className="level-header">
              <TrophyOutlined className="level-icon" style={{ color: currentLevel.color }} />
              <div className="level-info">
                <div className="level-title">
                  当前等级：<span style={{ color: currentLevel.color }}>{currentLevel.name}</span>
                </div>
                {nextLevel && (
                  <div className="level-next">
                    再获得 <span className="highlight">{pointsToNextLevel}</span> 金蝉值升级到
                    <span style={{ color: nextLevel.color }}> {nextLevel.name}</span>
                  </div>
                )}
              </div>
            </div>
            {nextLevel && (
              <Progress
                percent={upgradeProgress}
                strokeColor={currentLevel.color}
                showInfo={false}
                className="level-progress"
              />
            )}
          </Card>

          {/* 统计卡片 */}
          <Row gutter={[16, 16]} className="stats-row">
            <Col xs={12} sm={8}>
              <Card className="stat-card">
                <Statistic
                  title="金蝉值余额"
                  value={balance}
                  prefix={<WalletOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card className="stat-card">
                <Statistic
                  title="近期到期金蝉值"
                  value={balanceExpire}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                  suffix="点"
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card className="stat-card">
                <Statistic
                  title="累计消费金蝉值"
                  value={walletInfo?.totalConsumed || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 即将过期提醒 */}
          {expiringSoon.length > 0 && (
            <Alert
              message="金蝉值过期提醒"
              description={`您有 ${expiringSoon.length} 笔金蝉值即将在30天内过期，共计 ${expiringSoon.reduce((sum, item) => sum + item.remainPoints, 0)} 金蝉值`}
              type="warning"
              showIcon
              className="expiring-alert"
              icon={<ClockCircleOutlined />}
            />
          )}

          {/* 即将过期金蝉值列表 */}
          {expiringSoon.length > 0 && (
            <Card title="即将过期的金蝉值" bordered={false} className="expiring-card">
              <Table
                columns={expiringColumns}
                dataSource={expiringSoon}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </Card>
          )}
        </div>
      ),
    },
    {
      key: 'details',
      label: '金蝉值明细',
      children: (
        <Table
          columns={detailColumns}
          dataSource={pointDetails}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={handleTableChange}
          size={isMobile ? 'small' : 'middle'}
        />
      ),
    },
  ];

  return (
    <div className="page-container p-wallet-page">
      <div className="page-content">
        <div className="page-title-with-action">
          <div className="page-title">我的钱包</div>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadWalletData}
            loading={loading}
          >
            刷新
          </Button>
        </div>

        <Spin spinning={loading && !walletInfo}>
          <Card bordered={false} className="wallet-card">
            <Tabs items={tabItems} />
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default WalletPage;
